import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bookmark, MapPin, Trash2, Download } from "lucide-react";
import Navbar from "@/components/Navbar";
import CandidateDrawer from "@/components/CandidateDrawer";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAgency } from "@/lib/roles";
import { exportCandidatesHtml } from "@/lib/exportHtml";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const STAGES = ["saved", "contacted", "interviewing", "hired", "archived"];
const STAGE_STYLE = {
  saved: "text-slate-300 border-white/15",
  contacted: "text-cyan-300 border-cyan-500/30 bg-cyan-500/10",
  interviewing: "text-amber-300 border-amber-500/30 bg-amber-500/10",
  hired: "text-teal-300 border-teal-500/30 bg-teal-500/10",
  archived: "text-slate-500 border-white/10",
};

export default function SavedCandidates() {
  const { user } = useAuth();
  const agency = isAgency(user?.role);
  const [rows, setRows] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/saved-candidates").then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/employer/jobs").then((r) => setJobs(r.data)).catch(() => {});
    if (agency) api.get("/agency/clients").then((r) => setClients(r.data)).catch(() => {});
  }, [agency]);

  const patchMeta = (id, changes) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, saved_meta: { ...r.saved_meta, ...changes } } : r)));
  };

  const saveMeta = async (id, changes) => {
    patchMeta(id, changes);
    try { await api.put(`/saved-candidates/${id}`, changes); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const unsave = async (c) => {
    try {
      await api.post(`/candidates/${c.id}/save`);
      setRows((prev) => prev.filter((r) => r.id !== c.id));
      toast.success("Removed from talent pool");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleSaveFromDrawer = async (c) => {
    await unsave(c);
    setSelected(null);
  };

  return (
    <div className="min-h-screen wo-bg">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 pb-28 lg:pb-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400"><Bookmark size={20} /></div>
          <div>
            <h1 className="font-display font-bold text-3xl text-slate-100">Talent Pool</h1>
            <p className="text-slate-400 text-sm"><span className="font-mono text-teal-400" data-testid="saved-candidates-count">{rows.length}</span> saved candidates</p>
          </div>
          <Button onClick={() => exportCandidatesHtml(rows, "Talent Pool")} disabled={!rows.length} data-testid="pool-export-btn" variant="outline" className="ml-auto border-white/10 text-slate-200 hover:bg-white/5">
            <Download size={15} className="mr-2" /> Export HTML
          </Button>
        </div>

        {loading ? (
          <p className="text-slate-500 text-center py-20">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="text-center py-24 text-slate-500" data-testid="saved-candidates-empty">
            <Bookmark size={40} className="mx-auto text-slate-600 mb-4" /> No saved candidates yet. Save people from Talent Search.
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((c) => (
              <div key={c.id} data-testid="saved-candidate-row" className="liquid-glass rounded-xl p-4 flex flex-col lg:flex-row lg:items-center gap-4">
                <button onClick={() => setSelected(c)} className="flex items-center gap-3 min-w-0 flex-1 text-left">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-teal-400 to-blue-600 flex items-center justify-center text-sm font-bold text-slate-900 shrink-0">{c.name?.[0]?.toUpperCase()}</div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-100 truncate">{c.name}</div>
                    <div className="text-xs text-slate-400 truncate flex items-center gap-2">{c.headline} {c.location && <><span className="text-slate-600">·</span> <MapPin size={11} /> {c.location}</>}</div>
                  </div>
                </button>

                <div className="flex flex-wrap items-center gap-2">
                  <Select value={c.saved_meta?.stage || "saved"} onValueChange={(v) => saveMeta(c.id, { stage: v })}>
                    <SelectTrigger data-testid="saved-stage-select" className={`w-40 h-9 text-xs border ${STAGE_STYLE[c.saved_meta?.stage || "saved"]}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="text-slate-200">
                      {STAGES.map((st) => <SelectItem key={st} value={st} className="capitalize">{st}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input data-testid="saved-tags-input" defaultValue={(c.saved_meta?.tags || []).join(", ")}
                    onBlur={(e) => saveMeta(c.id, { tags: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
                    placeholder="tags" className="w-40 h-9 bg-slate-950/50 border-white/10 text-slate-100 text-xs" />
                  <Input data-testid="saved-notes-input" defaultValue={c.saved_meta?.notes || ""}
                    onBlur={(e) => saveMeta(c.id, { notes: e.target.value })}
                    placeholder="private note" className="w-48 h-9 bg-slate-950/50 border-white/10 text-slate-100 text-xs" />
                  <button onClick={() => unsave(c)} data-testid="saved-remove-btn" className="p-2 rounded-lg text-slate-500 hover:text-red-400 hover:bg-white/5 transition-colors"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <CandidateDrawer candidate={selected} isAgency={agency} jobs={jobs} clients={clients}
          onClose={() => setSelected(null)} onToggleSave={toggleSaveFromDrawer} />
      )}
    </div>
  );
}
